import React, {FunctionComponent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    Button,
    Container,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme
} from "@mui/material";
import {faChartBar, faSun} from "@fortawesome/free-solid-svg-icons";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import './stylesTrackers.css';
import {
    ACard,
    AChartX,
    AToggleButtonGroup,
    Period,
    PeriodSelector,
    Skeleton
} from "@atiautomacao/ati-ui-library";
import {useDispatch} from "react-redux";
import {getEntities, getNcus, isSameTrackerPeriod, normalizeNcuDayPeriod, NcuEquipment, prepareNcuEquipment, setPeriodTrackers, TrackerRequestKind} from "./TrackersReducer";
import {useAppSelector} from "../../../../Config/Hooks";
import {AppDispatch, RootState} from "../../../../Config/Store";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ChartNcu} from "../../../../Shared/Types/ChartNcu";
import {Tracker} from "../../../../Shared/Types/Tracker";
import {ChartSeriesType} from "../../../../Shared/Types/chartSeriesType";
import {useNavigate} from "react-router-dom";
import {setEquipment, setPowerPlant, setSkid} from "../../DashboardReducer";
import useInterval from "../../../../Shared/Hooks/useInterval";
import Box from "@mui/material/Box";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {useSnackbar} from "notistack";
import {CommandDTO, SendingTelecommandUnifilar} from "../../../../Shared/Components/SendingTelecommandUnifilar";
import {findAllCommands} from "../../../../Utils/CommandService"
import {format} from "date-fns";
import {COLORS} from "../../../../Utils/Colors";
import {truncateNumber} from "../../../../Utils/NumberUtil";
import {LocalDateTimeFormatISO} from "../../../../Utils/DateFormatPatternUtils";
import TrackerAvailabilityChart from "./TrackerAvailabilityChart";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import TrackerPositionHeatmap, {
    TrackerPositionPoint,
    TrackerPositionSeries
} from "./TrackerPositionHeatmap";
import TrackerPositionHeatmapModal from "./TrackerPositionHeatmapModal";

const defaultNcuPeriod = (): Period => ({
    groupBy: "day",
    fromDateTime: new Date(),
    toDateTime: new Date()
});

interface TrackerLoadCycle {
    powerPlantId: number;
    loadRequestId: string;
}

interface TrackerOption {
    id: number;
    name: string;
    nameSource: "navigation" | "fallback";
}

interface TrackerPositionContext {
    contextId: string;
    powerPlantId: number;
    loadRequestId: string;
    ncuId: number;
    periodKey: string;
}

interface TrackerRequestAttempt {
    requestId: string;
    contextId: string;
    kind: TrackerRequestKind;
    status: "pending";
    request: any;
}

type TrackerPositionViewMode = "curves" | "heatmap";

interface TrackerPositionSnapshot extends NcuEquipment {
    contextId: string;
    period: Period;
}

interface TrackerPositionView {
    contextId: string | null;
    status: "idle" | "loading" | "data" | "empty" | "error";
    snapshot: TrackerPositionSnapshot | null;
    refreshing: boolean;
    pendingRequestId: string | null;
}

const emptyPositionView = (): TrackerPositionView => ({
    contextId: null,
    status: "idle",
    snapshot: null,
    refreshing: false,
    pendingRequestId: null
});

const parsePositionValue = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "null" || value === "") {
        return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const hasFiniteTrackerPoint = (chart?: ChartNcu | null) => chart?.series?.some((series: any) => (
    (series.data ?? series.dataByTime ?? []).some((point: [string, unknown]) => (
        parsePositionValue(point?.[1]) !== null
    ))
)) ?? false;

const toTrackerPositionPoints = (source: unknown): TrackerPositionPoint[] | undefined => {
    if (!Array.isArray(source)) {
        return undefined;
    }

    return source.reduce<TrackerPositionPoint[]>((points, point) => {
        if (!Array.isArray(point) || point.length < 2) {
            return points;
        }
        const timestamp = point[0];
        if (!(timestamp instanceof Date) &&
            typeof timestamp !== "string" &&
            typeof timestamp !== "number") {
            return points;
        }
        points.push([timestamp, point[1]]);
        return points;
    }, []);
};

const toTrackerPositionSeries = (chart?: ChartNcu | null): TrackerPositionSeries[] => (
    (chart?.series ?? []).map(series => {
        const runtimeSeries = series as unknown as {
            name?: string | null;
            data?: unknown;
            dataByTime?: unknown;
        };
        return {
            name: runtimeSeries.name,
            data: toTrackerPositionPoints(runtimeSeries.data),
            dataByTime: toTrackerPositionPoints(runtimeSeries.dataByTime)
        };
    })
);

const sortTrackerOptions = (options: TrackerOption[]) => [...options].sort((left, right) => {
    const byName = left.name.localeCompare(right.name, undefined, {numeric: true, sensitivity: "base"});
    return byName !== 0 ? byName : left.id - right.id;
});

const TrackersPage: FunctionComponent<{powerPlantId: number}> = ({powerPlantId}) => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const {periodTrackers} = useAppSelector((state: RootState) => state.trackers);
    const dispatch = useDispatch<AppDispatch>();
    const [trackerOptions, setTrackerOptions] = useState<TrackerOption[]>([]);
    const [selectedNcuId, setSelectedNcuId] = useState<number | null>(null);
    const [listOwner, setListOwner] = useState<TrackerLoadCycle | null>(null);
    const [listStatus, setListStatus] = useState<"loading" | "ready" | "error">("loading");
    const [positionContext, setPositionContext] = useState<TrackerPositionContext | null>(null);
    const [positionView, setPositionView] = useState<TrackerPositionView>(emptyPositionView);
    const [positionMode, setPositionMode] = useState<TrackerPositionViewMode>("curves");
    const navigate = useNavigate();
    const { entities } = useAppSelector((state: RootState) => state.dashboardNavigationMenu);
    const [commandDTOList, setCommandDTOList] = useState<CommandDTO[]>([])
    const [open, setOpen] = useState(false)
    const [isHeatmapModalOpen, setIsHeatmapModalOpen] = useState(false);
    const [equipmentId, setEquipmentId] = useState(-1)
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    
    const powerStationEntity = useMemo(
        () => entities.find(entity => entity.id === powerPlantId) ?? null,
        [entities, powerPlantId]
    );
    const powerStationEntityRef = useRef(powerStationEntity);
    powerStationEntityRef.current = powerStationEntity;
    const normalizedPeriod = useMemo(
        () => normalizeNcuDayPeriod(periodTrackers ?? defaultNcuPeriod()),
        [periodTrackers]
    );
    const periodKey = `${LocalDateTimeFormatISO(normalizedPeriod.fromDateTime)}|${LocalDateTimeFormatISO(normalizedPeriod.toDateTime)}`;
    const activeLoadCycleRef = useRef<TrackerLoadCycle | null>(null);
    const officialNcuIdsRef = useRef<Set<number>>(new Set());
    const activeContextRef = useRef<TrackerPositionContext | null>(null);
    const activeAttemptRef = useRef<TrackerRequestAttempt | null>(null);
    const positionCardRef = useRef<HTMLDivElement | null>(null);
    const heatmapReturnFocusRef = useRef<HTMLButtonElement | null>(null);
    const pendingHeatmapFocusRestoreRef = useRef(false);
    const contextSequenceRef = useRef(0);
    const positionViewRef = useRef(positionView);
    const periodKeyRef = useRef(periodKey);
    periodKeyRef.current = periodKey;
    const hasCurrentNcuList = listOwner?.powerPlantId === powerPlantId &&
        listOwner.loadRequestId === activeLoadCycleRef.current?.loadRequestId;
    const visibleTrackerOptions = hasCurrentNcuList ? trackerOptions : [];
    const selectedTrackerOption = visibleTrackerOptions.find(option => option.id === selectedNcuId) ?? null;

    const updatePositionView = useCallback((updater: (current: TrackerPositionView) => TrackerPositionView) => {
        const next = updater(positionViewRef.current);
        positionViewRef.current = next;
        setPositionView(next);
    }, []);

    const abortActiveAttempt = useCallback(() => {
        const attempt = activeAttemptRef.current;
        activeAttemptRef.current = null;
        attempt?.request?.abort?.();
    }, []);

    const getNavigationEquipmentName = useCallback((id: number) => {
        const name = powerStationEntityRef.current?.equipmentList?.find(equipment => equipment.id === id)?.name;
        return typeof name === "string" && name.trim() ? name.trim() : undefined;
    }, []);

    const requestNcuDetail = useCallback((
        context: TrackerPositionContext,
        kind: TrackerRequestKind,
        requestPeriod: Period
    ) => {
        const loadCycle = activeLoadCycleRef.current;
        if (activeAttemptRef.current ||
            activeContextRef.current?.contextId !== context.contextId ||
            loadCycle?.powerPlantId !== context.powerPlantId ||
            loadCycle.loadRequestId !== context.loadRequestId ||
            periodKeyRef.current !== context.periodKey ||
            !officialNcuIdsRef.current.has(context.ncuId)) {
            return null;
        }

        const request = dispatch(getEntities({
            powerPlantId: context.powerPlantId,
            ncuId: context.ncuId,
            periodTrackers: requestPeriod,
            loadRequestId: context.loadRequestId,
            contextId: context.contextId,
            requestKind: kind
        }));
        activeAttemptRef.current = {
            requestId: request.requestId,
            contextId: context.contextId,
            kind,
            status: "pending",
            request
        };

        updatePositionView(current => kind === "refresh" ? {
            ...current,
            contextId: context.contextId,
            refreshing: true,
            pendingRequestId: request.requestId
        } : {
            contextId: context.contextId,
            status: "loading",
            snapshot: null,
            refreshing: false,
            pendingRequestId: request.requestId
        });

        request.then((action: any) => {
            const activeCycle = activeLoadCycleRef.current;
            const currentContext = activeContextRef.current;
            const currentAttempt = activeAttemptRef.current;
            const ownsResult = currentContext?.contextId === context.contextId &&
                currentContext.powerPlantId === context.powerPlantId &&
                currentContext.loadRequestId === context.loadRequestId &&
                currentContext.ncuId === context.ncuId &&
                currentContext.periodKey === context.periodKey &&
                activeCycle?.powerPlantId === context.powerPlantId &&
                activeCycle.loadRequestId === context.loadRequestId &&
                currentAttempt?.contextId === context.contextId &&
                currentAttempt.requestId === request.requestId &&
                periodKeyRef.current === context.periodKey &&
                officialNcuIdsRef.current.has(context.ncuId);

            if (!ownsResult) {
                return;
            }

            activeAttemptRef.current = null;
            const responseData = getEntities.fulfilled.match(action) ? action.payload?.data?.data : null;
            const responseHasAnotherId = responseData?.id !== undefined && Number(responseData.id) !== context.ncuId;
            const errorData = action?.error?.data;
            const validNoData = getEntities.fulfilled.match(action)
                ? !responseData
                : Number(errorData?.id) === context.ncuId;

            if (responseHasAnotherId) {
                updatePositionView(current => kind === "refresh" ? {
                    ...current,
                    refreshing: false,
                    pendingRequestId: null
                } : {
                    contextId: context.contextId,
                    status: "error",
                    snapshot: null,
                    refreshing: false,
                    pendingRequestId: null
                });
                return;
            }

            if (validNoData) {
                updatePositionView(() => ({
                    contextId: context.contextId,
                    status: "empty",
                    snapshot: null,
                    refreshing: false,
                    pendingRequestId: null
                }));
                return;
            }

            if (getEntities.fulfilled.match(action) && responseData) {
                const prepared = prepareNcuEquipment(responseData);
                const snapshot: TrackerPositionSnapshot = {
                    ...prepared,
                    contextId: context.contextId,
                    period: requestPeriod
                };
                updatePositionView(() => ({
                    contextId: context.contextId,
                    status: hasFiniteTrackerPoint(prepared.chart) ? "data" : "empty",
                    snapshot: hasFiniteTrackerPoint(prepared.chart) ? snapshot : null,
                    refreshing: false,
                    pendingRequestId: null
                }));
                return;
            }

            if (kind === "refresh") {
                updatePositionView(current => ({
                    ...current,
                    refreshing: false,
                    pendingRequestId: null
                }));
                enqueueSnackbar("Não foi possível atualizar os dados.", {variant: "error"});
            } else {
                updatePositionView(() => ({
                    contextId: context.contextId,
                    status: "error",
                    snapshot: null,
                    refreshing: false,
                    pendingRequestId: null
                }));
            }
        });

        return request;
    }, [dispatch, enqueueSnackbar, updatePositionView]);

    useEffect(() => {
        abortActiveAttempt();
        activeContextRef.current = null;
        setPositionContext(null);
        positionViewRef.current = emptyPositionView();
        setPositionView(positionViewRef.current);
        officialNcuIdsRef.current = new Set();
        setTrackerOptions([]);
        setSelectedNcuId(null);
        setListOwner(null);
        setListStatus("loading");
        const request = dispatch(getNcus(powerPlantId));
        const loadCycle = {powerPlantId, loadRequestId: request.requestId};
        activeLoadCycleRef.current = loadCycle;

        request.then((action) => {
            const activeCycle = activeLoadCycleRef.current;
            if (activeCycle?.powerPlantId !== loadCycle.powerPlantId ||
                activeCycle.loadRequestId !== loadCycle.loadRequestId) {
                return;
            }

            setListOwner(loadCycle);
            if (getNcus.fulfilled.match(action)) {
                const ids = Array.isArray(action.payload?.data?.data) ? action.payload.data.data : [];
                const uniqueIds = Array.from(new Set<number>(ids.map(Number).filter(Number.isFinite)));
                officialNcuIdsRef.current = new Set(uniqueIds);
                const options = sortTrackerOptions(uniqueIds.map(id => {
                    const navigationName = getNavigationEquipmentName(id);
                    return {
                        id,
                        name: navigationName ?? `NCU ${id}`,
                        nameSource: navigationName ? "navigation" : "fallback"
                    };
                }));
                setTrackerOptions(options);
                setSelectedNcuId(options[0]?.id ?? null);
                setListStatus("ready");
            } else {
                setTrackerOptions([]);
                setSelectedNcuId(null);
                setListStatus("error");
            }
        });

        return () => {
            if (activeLoadCycleRef.current?.loadRequestId === loadCycle.loadRequestId) {
                activeLoadCycleRef.current = null;
            }
            request.abort?.();
        };
    }, [abortActiveAttempt, dispatch, getNavigationEquipmentName, powerPlantId]);

    useLayoutEffect(() => {
        const activeCycle = activeLoadCycleRef.current;
        if (!activeCycle || !hasCurrentNcuList || selectedNcuId === null || !periodKey) {
            abortActiveAttempt();
            activeContextRef.current = null;
            setPositionContext(null);
            positionViewRef.current = emptyPositionView();
            setPositionView(positionViewRef.current);
            return;
        }

        const activeContext = activeContextRef.current;
        if (activeContext?.powerPlantId === powerPlantId &&
            activeContext.loadRequestId === activeCycle.loadRequestId &&
            activeContext.ncuId === selectedNcuId &&
            activeContext.periodKey === periodKey) {
            return;
        }

        abortActiveAttempt();
        const context: TrackerPositionContext = {
            contextId: `tracker-position-${++contextSequenceRef.current}`,
            powerPlantId,
            loadRequestId: activeCycle.loadRequestId,
            ncuId: selectedNcuId,
            periodKey
        };
        activeContextRef.current = context;
        setPositionContext(context);
        positionViewRef.current = {
            contextId: context.contextId,
            status: "loading",
            snapshot: null,
            refreshing: false,
            pendingRequestId: null
        };
        setPositionView(positionViewRef.current);
        requestNcuDetail(context, "initial", normalizedPeriod);
    }, [abortActiveAttempt, hasCurrentNcuList, normalizedPeriod, periodKey, powerPlantId, requestNcuDetail, selectedNcuId]);

    useInterval(() => {
        const context = activeContextRef.current;
        if (!context || activeAttemptRef.current || periodKeyRef.current !== context.periodKey) {
            return;
        }
        const kind: TrackerRequestKind = positionViewRef.current.status === "data" ||
            positionViewRef.current.status === "empty" ? "refresh" : "initial";
        requestNcuDetail(context, kind, normalizedPeriod);
    }, userConfig.mainUpdateInterval); //1 minute

    useEffect(() => {
        return abortActiveAttempt;
    }, [abortActiveAttempt]);

    const handleNcuPeriodChange = (newPeriod: Period) => {
        const normalized = normalizeNcuDayPeriod(newPeriod);

        if (isSameTrackerPeriod(periodTrackers, normalized)) {
            return;
        }

        dispatch(setPeriodTrackers(normalized));
    };

    const handlePositionModeChange = (
        _event: React.MouseEvent<HTMLElement>,
        nextMode: string | null
    ) => {
        if (nextMode === "curves" || nextMode === "heatmap") {
            setPositionMode(nextMode);
        }
    };

    const findPositionCardActionsButton = useCallback(() => (
        positionCardRef.current?.querySelector<HTMLButtonElement>(
            'button[aria-haspopup="true"]'
        ) ?? null
    ), []);

    const handleOpenHeatmapModal = () => {
        heatmapReturnFocusRef.current = findPositionCardActionsButton();
        pendingHeatmapFocusRestoreRef.current = false;
        setIsHeatmapModalOpen(true);
    };

    const handleCloseHeatmapModal = useCallback(() => {
        pendingHeatmapFocusRestoreRef.current = true;
        setIsHeatmapModalOpen(false);
    }, []);

    const restoreHeatmapFocus = useCallback(() => {
        const currentActionsButton = findPositionCardActionsButton();
        const previousActionsButton = heatmapReturnFocusRef.current;
        const target = currentActionsButton ?? (
            previousActionsButton?.isConnected ? previousActionsButton : null
        );

        if (target) {
            target.focus();
            heatmapReturnFocusRef.current = target;
            pendingHeatmapFocusRestoreRef.current = false;
        }
    }, [findPositionCardActionsButton]);

    const ncuPeriod = useMemo((): Period => {
        return {
            groupBy: "day",
            fromDateTime: normalizedPeriod.fromDateTime,
            toDateTime: normalizedPeriod.toDateTime
        };
    }, [normalizedPeriod]);

    const handleCloseModal = () => {
        setOpen(false)
        setCommandDTOList([]);
        setEquipmentId(-1)
    };


    useEffect(() => {
        if (equipmentId !== -1) {
            findAllCommands(equipmentId).then(
                (response:any) => {
                    if (response.status === 204) {
                        setCommandDTOList([]);
                        enqueueSnackbar("Comandos não encontrados!", {variant: "error", autoHideDuration: 2000});
                    } else {
                        setCommandDTOList(response.data.data);
                    }
                }
            ).catch(
                (error:any) => {
                setCommandDTOList([]);
                    enqueueSnackbar(`Erro ao carregar comandos: ${error.message}`, { variant: "error",autoHideDuration: 2000 });
            })
        }else{
            setCommandDTOList([]);
        }
    }, [equipmentId, enqueueSnackbar]);

    const toOpen = (id:number) => {
        setEquipmentId(id)
        setOpen(true)
    }

    function navigateTo(ncu: NcuEquipment){
        let skidEq = null;
        let equipment = null;
        if(powerStationEntity){
            powerStationEntity.skidList.forEach(skid => {
                equipment = skid.equipmentList.find(eq => eq.id === ncu.id)
                if(equipment){
                    skidEq = skid;
                    dispatch(setPowerPlant(powerStationEntity));
                    dispatch(setSkid(skidEq));
                    dispatch(setEquipment(equipment));
                }
            })
        }

        navigate(`/dashboard/power-plant/skid/equipments/details`);
    }

    const headTitle = ['Tracker', 'Posição Alvo', 'Posição do Tracker'];

    const calculateValueMax = (value: number) => {
        if(value === 0){
            return 1;
        } else if(value > 0){
            return truncateNumber(value + (value * 15)/100);
        }
        return truncateNumber(value - (value * 15)/100);
    }

    const parseTrackerChartValue = parsePositionValue;

    function createOptions(chart: ChartNcu | null){
        let nameList: string[] = [];
        chart?.series && chart.series.forEach((s: ChartSeriesType) => {
            if (s.name != null) {
                nameList.push(s.name);
            }
        });

        let series = chart?.series ? chart.series.map((serie: any) => ({
            ...serie,
            showSymbol: false,
            connectNulls: false,
            data: (serie.data ?? serie.dataByTime ?? []).map(([time, value]: [string, unknown]) => [
                time,
                parseTrackerChartValue(value)
            ])
        })) : [];
        let windowSize = window.innerWidth;
        const colors = series.map((_, i) => COLORS[i % COLORS.length]);
        let globalMaxValue = -Infinity;
        let globalMinValue = Infinity;
        series.forEach(serie => {
            if(serie.data) {
                const validData = serie.data
                    .map((element: [string, unknown]) => parseTrackerChartValue(element[1]))
                    .filter((value: number | null): value is number => value !== null);

                if (validData.length > 0) {
                    const serieMaxValue = Math.max(...validData);
                    const serieMinValue = Math.min(...validData);
                    globalMaxValue = Math.max(globalMaxValue, serieMaxValue);
                    globalMinValue = Math.min(globalMinValue, serieMinValue);
                }
            }
        });

        const hasValidRange = Number.isFinite(globalMaxValue) && Number.isFinite(globalMinValue);

        // @ts-ignore
        const options: AChartXProps['option'] = {
            legend: {
                type: 'scroll',
                orient: windowSize < 1050 ? 'horizontal' : 'vertical',
                right: 4,
                top: 25,
                bottom: 0,
                height: 375,
                data: nameList
            },
            
            xAxis: chart?.xAxis ? chart?.xAxis[0] : {},
            yAxis: chart?.yAxis ? {
                ...chart?.yAxis[0],
                max: hasValidRange && globalMaxValue < 180
                    ? truncateNumber(calculateValueMax(globalMaxValue))
                    : (chart.yAxis[0].max ?? 180),
                min: hasValidRange && globalMinValue > -180
                    ? truncateNumber(globalMinValue - Math.abs(globalMinValue * 15) / 100)
                    : (chart.yAxis[0].min ?? -180),
                interval: truncateNumber(180 / 7)
            } : {},
            series: series,
            color: colors,
            tooltip: {
                trigger: "axis",
                triggerOn: "mousemove",
                appendToBody: true,
                enterable: true,
                confine: true,
                alwaysShowContent: false,
                className: 'custom-tooltip',
                position: function (point: any) {
                    return [point[0] - 160, point[1]];
                },
                formatter: function (params: any) {
                    let formatDate = "HH:mm";
                    let dateStr = format(new Date(params[0].axisValue), formatDate);

                    let content = `
                        <table style="border-collapse: collapse; display: inline-block; margin-right: 10px;">
                            <tr>
                                <th style="padding: 5px; text-align: left;">Série</th>
                                <th style="padding: 5px; text-align: right;">Valor</th>
                            </tr>
                            ${params
                        .map(
                            (item: any) => `
                            <tr>
                                <td style="padding: 5px; text-align: left;">${item.marker} ${item.seriesName}</td>
                                <td style="padding: 5px; text-align: right;"><b>${item.value[1]}</b></td>
                            </tr>`
                        )
                        .join("")}
                        </table>`

                    return `
                        <div  style="pointer-events: all; padding: 2px; text-align: right;">${dateStr}</div>
                        <div style="padding: 5px;
                                    border: 1px solid #ddd;
                                    max-height: 250px;
                                    overflow-y: auto;
                                    white-space: nowrap;
                                    display: block;
                                    pointer-events: all;
                                    ">
                            ${content}
                        </div>`;
                },
                backgroundColor: "rgb(255,255,255)",
                textStyle: { color: "#000" },
                borderWidth: 0,
            },
            toolbox: {
                right: '6%',
                itemSize: 25,
                top: 40,
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: {}
                }
            },
            dataZoom: [
                {
                    type: 'inside',
                    throttle: 50
                },
            ],
        }
        return options;
    }

    function createOptionsSpeed(ncu: any) {
        // @ts-ignore
        const optionSpeed: AChartXProps['option'] = {
            series: [
                {
                    type: 'gauge',
                    min: 0,
                    max: 240,
                    axisLine: {
                        lineStyle: {
                            width: 10,
                            color: [
                                [0.3, '#67e0e3'],
                                [0.7, '#37a2da'],
                                [1, '#fd666d']
                            ]
                        }
                    },
                    pointer: {
                        itemStyle: {
                            color: 'auto'
                        }
                    },
                    axisTick: {
                        distance: -10,
                        length: 8,
                        lineStyle: {
                            color: '#fff',
                            width: 2
                        }
                    },
                    splitLine: {
                        distance: -45,
                        length: 20,
                        lineStyle: {
                            color: '#fff',
                            width: 2
                        }
                    },
                    axisLabel: {
                        color: 'inherit',
                        distance: 40,
                        fontSize: 8
                    },
                    detail: {
                        valueAnimation: true,
                        formatter: '{value} km/h',
                        color: 'inherit',
                        fontSize: 10
                    },
                    data: [
                        {
                            value: ncu.windSpeed
                        }
                    ]
                }
            ]
        };

        return optionSpeed;
    }

    function createOptionDirection(ncu: any) {
        // @ts-ignore
        const optionDirection: AChartXProps['option'] = {
            grid: {
                top: -25,
                // bottom: 50
                // bottom: 30,
                // left: 0,
                // top: 0,
                // right: 0,
                // bottom: 0,
                // width: '100px',
                // height: '100px',
                // x: 40,
                // y: 20,
                // x2: 40,
                // y2: 20
            },
            toolbox: {
                // bottom: 50,
                top: -10,
                // top: 28,
                // itemSize: 15,
                
            },
            tooltip:{
                trigger: 'item',
                borderWidth: 0,
                renderMode: 'richText',
            },
            series: [
                {
                    name: 'Direção do Vento',
                    type: 'gauge',
                    startAngle: 90,
                    endAngle: -270,
                    min: 0,
                    max: 360,
                    detail: {
                        valueAnimation: true,
                        formatter: '{value} °',
                        fontSize: 10
                    },
                    axisLabel: {
                        show: false
                    },
                    axisTick: {
                        distance: 0,
                        length: 5,
                        lineStyle: {
                            color: '#bcbcbc',
                            width: 2
                        }
                    },
                    axisLine: {
                        fontSize: 10
                    },
                    splitLine: {
                        distance: 5,
                        length: 5,
                        lineStyle: {
                            color: 'black',
                            width: 2
                        }
                    },
                    title: {
                        fontSize: 8
                    },
                    pointer: {
                        icon: 'path://M12.8,0.7l12,40.1H0.7L12.8,0.7z',
                        length: '15%',
                        width: 10,
                        offsetCenter: [0, '-40%'],
                        itemStyle: {
                            color: 'blue'
                        }
                    },
                    data: [
                        { value: ncu.windDirection }
                    ]
                }
            ]
        };

        return optionDirection;
    }

    function createTable(trackers: Tracker[]){
        let numTabelas = 1;
        let tabelas = [[]];

        if(window.innerWidth > 770 && window.innerWidth < 1400) {
            tabelas.push([]);
            numTabelas = 2;
        }
        if(window.innerWidth > 1400) {
            tabelas.push([]);
            tabelas.push([]);
            numTabelas = 3;
        }

        for (let i = 0; i < trackers?.length; i++) {
            const grupoIndex = i % numTabelas;
            // @ts-ignore
            tabelas[grupoIndex].push(trackers[i]);
        }

        return (
            <div className="trackersTable">
                {
                    tabelas && tabelas.length > 0 && tabelas.map((tabela: any, index: number) => {
                        return (
                            <TableContainer key={index.valueOf()}>
                                <Table stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            {headTitle.map((title) => {
                                                return (
                                                    <TableCell
                                                        key={title}
                                                        align={"center"}
                                                        style={{
                                                            backgroundColor: "rgba(4,104,190,0.92)",
                                                            color: "white"
                                                        }}
                                                    >
                                                        {title}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tabelas && tabelas.length > 0 && tabela.map((row: any, i: number) => (
                                            <TableRow
                                                key={row.tracker}
                                                sx={{'&:last-child td, &:last-child th': {border: 0},
                                                    backgroundColor: row.alarmed ? '#F85F4E' : 'inherit' // Vermelho claro se estiver alarmado
                                                }}
                                            >
                                                <TableCell
                                                    align="left"
                                                    component="th"
                                                    scope="row"
                                                    sx={{display: 'flex', justifyContent: 'space-between'}}
                                                >
                                                    <FontAwesomeIcon icon={faSun} style={{
                                                        color: 'gray'
                                                    }}/>
                                                    {row.name}
                                                </TableCell>
                                                <TableCell
                                                    align="center">{row.lastSunPosition == null ? '-' : row.lastSunPosition + '°'}</TableCell>
                                                <TableCell
                                                    align="center">{row.lastTrackerPosition == null ? '-' : row.lastTrackerPosition + '°'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    })
                }
            </div>
        )
    }

    const skeletonToRender = <Skeleton animation="wave" variant="rounded" height={800} width="100%" />;
    const hasCurrentPositionContext = positionContext?.powerPlantId === powerPlantId &&
        positionContext.loadRequestId === listOwner?.loadRequestId &&
        positionContext.ncuId === selectedNcuId &&
        positionContext.periodKey === periodKey &&
        positionView.contextId === positionContext.contextId;
    const visibleSnapshot = hasCurrentPositionContext && positionView.status === "data"
        ? positionView.snapshot
        : null;
    const visiblePositionSeries = useMemo(
        () => toTrackerPositionSeries(visibleSnapshot?.chart),
        [visibleSnapshot]
    );

    useEffect(() => {
        if (!isHeatmapModalOpen &&
            pendingHeatmapFocusRestoreRef.current &&
            hasCurrentPositionContext &&
            positionView.status === "data") {
            restoreHeatmapFocus();
        }
    }, [
        hasCurrentPositionContext,
        isHeatmapModalOpen,
        positionView.status,
        restoreHeatmapFocus
    ]);
    const emptyState = (
        <Container fixed>
            <Box sx={{bgcolor: '#e1e0e0', height: '500px', width: '100%'}}>
                <DataNotFound
                    boxStyle={{height: '500px', width: '100%'}}
                    titleStyle={{fontSize: '23px', color: '#646363'}}
                />
            </Box>
        </Container>
    );
    const errorState = (
        <Container fixed>
            <Box sx={{bgcolor: '#e1e0e0', height: '500px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography sx={{fontSize: '23px', color: '#646363'}}>
                    Não foi possível carregar os dados.
                </Typography>
            </Box>
        </Container>
    );
    const tablesToRender = selectedTrackerOption && hasCurrentPositionContext && (
            <div
                key={`${powerPlantId}-${selectedTrackerOption.id}-${periodKey}`}
                className="tracker-position-card-shell"
            >
                <ACard
                    cardRef={positionCardRef}
                    key={`chart-power-trackers-${openSubMenu}-${powerPlantId}-${selectedTrackerOption.id}-${periodKey}`}
                    title="Histórico de Posicionamento"
                    subTitle={(
                        <span className="tracker-position-subtitle">
                            Para uma visualização completa, clique no botão
                            <MoreHorizOutlinedIcon
                                className="tracker-position-subtitle-more-icon"
                                aria-hidden="true"
                            /> Ver mais, e selecione
                            <FullscreenOutlinedIcon
                                className="tracker-position-subtitle-expand-icon"
                                aria-hidden="true"
                            /> Modo expandido.
                        </span>
                    ) as unknown as string}
                    ariaLabel={`${powerStationEntity?.name ?? "Usina"} - ${selectedTrackerOption.name}`}
                    iconTitle={{
                        showIcon: true,
                        kind: "mui",
                        iconMaterialComponent: ShareLocationIcon,
                        iconTitleStyle: {
                            width: "35px",
                            height: "35px",
                            padding: "3px"
                        }
                    }}
                    styleProps={{
                        cardStyle: {
                            borderRadius: "8px"
                        },
                        headerStyle: {
                            padding: "12px 8px 8px"
                        },
                        cardTitleStyle: {
                            gap: "8px",
                            padding: "8px"
                        },
                        titleStyle: {
                            fontFamily: theme.typography.fontFamily,
                            fontSize: "20px",
                            fontWeight: 500,
                            lineHeight: 1.6,
                            letterSpacing: "0.15px"
                        },
                        subTitleStyle: {
                            padding: "0 8px 8px",
                            marginTop: "4px",
                            fontFamily: theme.typography.fontFamily,
                            fontSize: "14px",
                            lineHeight: 1.43,
                            letterSpacing: "0.17px",
                            color: "rgba(0, 0, 0, 0.87)"
                        },
                        contentStyle: {
                            display: "flex",
                            flexDirection: "column",
                            padding: positionMode === "heatmap" ? "0 16px 12px" : "16px"
                        }
                    }}
                    headerControlsPosition={"header"}
                    headerControls={
                        <Box
                            className="tracker-position-header-controls"
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <AToggleButtonGroup
                                title="Visualização da posição"
                                options={[
                                    {
                                        value: "curves",
                                        label: "Gráfico",
                                        ariaLabel: "Gráfico"
                                    },
                                    {
                                        value: "heatmap",
                                        label: "Mapa de calor",
                                        ariaLabel: "Mapa de calor"
                                    }
                                ]}
                                value={positionMode}
                                exclusive
                                onChange={handlePositionModeChange}
                                styleProps={{
                                    toggleButtonGroup: {
                                        width: "204px",
                                        height: "38px",
                                        borderRadius: "8px"
                                    },
                                    toggleButton: {
                                        height: "38px",
                                        padding: "8px"
                                    },
                                    typography: {
                                        fontFamily: theme.typography.fontFamily,
                                        fontWeight: 500,
                                        fontSize: "13px",
                                        lineHeight: "22px",
                                        letterSpacing: "0.46px",
                                        textTransform: "uppercase"
                                    }
                                }}
                            />
                            <Box className="tracker-position-day-control">
                                <span className="tracker-position-day-label">Dia</span>
                                <span className="tracker-position-day-value">
                                    {format(ncuPeriod.toDateTime, "dd/MM/yyyy")}
                                </span>
                                <PeriodSelector
                                    key={periodKey}
                                    styleProps={{
                                        base: {
                                            display: "flex",
                                            alignItems: "center"
                                        }
                                    }}
                                    mode="button"
                                    hideDatetime={false}
                                    hideGroup
                                    disableHourSelector
                                    inputFormat="dd/MM/yyyy"
                                    periodInterval={{
                                        day: {
                                            date: ncuPeriod.toDateTime,
                                            startTime: "04:00:00",
                                            endTime: "21:00:00"
                                        }
                                    }}
                                    period={ncuPeriod}
                                    onChange={handleNcuPeriodChange}
                                    groupByOptions={["day"]}
                                />
                            </Box>
                            <Button
                                variant="contained"
                                size={"small"}
                                onClick={() => {toOpen(selectedTrackerOption.id)}}
                                endIcon={<SendIcon/>}
                                sx={{
                                    minWidth: "109.81px",
                                    height: "34px",
                                    borderRadius: "4px",
                                    backgroundColor: "#2959eb",
                                    textTransform: "capitalize",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    letterSpacing: "0.371px",
                                    lineHeight: "22.75px"
                                }}
                            >
                                Comandos
                            </Button>
                        </Box>
                    }

                    headerActions={
                        <Stack direction="column" spacing="8px">
                            <MenuItem
                                autoFocus
                                onClick={handleOpenHeatmapModal}
                                sx={{borderRadius: "4px"}}
                            >
                                <ListItemIcon>
                                    <FullscreenOutlinedIcon fontSize="small"/>
                                </ListItemIcon>
                                <ListItemText>Modo expandido</ListItemText>
                            </MenuItem>
                            <Divider/>
                            <MenuItem
                                disabled={!visibleSnapshot}
                                onClick={() => visibleSnapshot && navigateTo(visibleSnapshot)}
                                sx={{borderRadius: "4px"}}
                            >
                                <ListItemIcon>
                                    <FontAwesomeIcon icon={faChartBar}/>
                                </ListItemIcon>
                                <ListItemText>Equipamento</ListItemText>
                            </MenuItem>
                        </Stack>
                    }
                    InformationTooltip={
                        <Box
                            className="tracker-position-info"
                            aria-label="Informações do histórico de posicionamento"
                        >
                            <InfoOutlinedIcon/>
                        </Box>
                    }
                >
                    {
                        visibleSnapshot ?
                            positionMode === "heatmap" ? (
                                <Box className="tracker-position-heatmap-view">
                                    <TrackerPositionHeatmap
                                        contextId={visibleSnapshot.contextId}
                                        series={visiblePositionSeries}
                                        period={{
                                            start: visibleSnapshot.period.fromDateTime,
                                            end: visibleSnapshot.period.toDateTime
                                        }}
                                        isMobile={window.innerWidth < 771}
                                    />
                                </Box>
                            ) : (
                            <>
                                <Box className="ncuGraphs">
                                    <Box className="windStyle">
                                        <Box sx={{width: "100%"}}>
                                            <AChartX
                                                option={createOptionsSpeed(visibleSnapshot)}
                                                height={175}
                                                loading={false}
                                                style={{marginTop: '-2%'}}
                                                theme={theme.palette.mode}
                                            />
                                            <Typography style={{marginLeft: '22%', marginTop: '-5%'}} variant="subtitle1" fontSize={12} color={"#000"}>Velocidade do Vento</Typography>
                                        </Box>
                                        <Box sx={{width: "100%"}}>
                                            <AChartX
                                                option={createOptionDirection(visibleSnapshot)}
                                                height={175}
                                                loading={false}
                                                style={{marginTop: '-2%'}}
                                                theme={theme.palette.mode}
                                            />
                                            <Typography style={{marginLeft: '25%', marginTop: '2%'}} variant="subtitle1" fontSize={12} color={"#000"}>Direção do Vento</Typography>
                                        </Box>
                                    </Box>
                                    <Box className="tracker-position-view">
                                        <Box data-testid="tracker-position-curves">
                                            <AChartX
                                                key={`ncu-line-chart-${selectedTrackerOption.id}-${periodKey}`}
                                                option={createOptions(visibleSnapshot.chart)}
                                                width={window.innerWidth < 1050 ? "123%" : "112%"}
                                                height={450}
                                                loading={false}
                                                style={{marginTop: '2%', marginLeft: window.innerWidth < 1050 ? '0%' : '-9%'}}
                                                theme={theme.palette.mode}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                                <Accordion style={{marginTop: 0}}>
                                    <AccordionSummary
                                        expandIcon={<ArrowDropDownIcon/>}
                                        aria-controls="panel2-content"
                                        id="panel2-header"
                                    >
                                        <Typography>Trackers</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {createTable(visibleSnapshot.trackers)}
                                    </AccordionDetails>
                                </Accordion>
                            </>
                            )
                        :
                        positionView.status === "error" ? errorState : emptyState
                    }
                </ACard>
            </div>
        );

    const positionChartToRender = !hasCurrentNcuList || listStatus === "loading" ||
        (visibleTrackerOptions.length > 0 && (!hasCurrentPositionContext || positionView.status === "loading" || positionView.status === "idle"))
        ? skeletonToRender
        : listStatus === "error"
            ? errorState
            : visibleTrackerOptions.length === 0
                ? emptyState
                : tablesToRender;
    
    // @ts-ignore
    return (
        <>
        <Grid>
            <FormControl sx={{m: 1, width: '100%'}}>
                <InputLabel id="ncu-checkbox-label">Tracker</InputLabel>
                <Select
                    labelId="ncu-checkbox-label"
                    id="ncu-checkbox"
                    value={selectedTrackerOption ? selectedTrackerOption.id : ''}
                    onChange={(e) => {
                        const selectedId = Number(e.target.value);
                        setSelectedNcuId(visibleTrackerOptions.some(option => option.id === selectedId) ? selectedId : null);
                    }}
                >
                    {visibleTrackerOptions.map((ncu) => (
                        <MenuItem key={ncu.id} value={ncu.id}
                            color={"default"}
                        >{ncu.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TrackerAvailabilityChart powerPlantId={powerPlantId}/>
                {positionChartToRender}
            </Box>
        </Grid>
        {commandDTOList.length > 0 &&
        <SendingTelecommandUnifilar
            open={open}
            onCloseModal={handleCloseModal}
            commandDTOList={commandDTOList}
        />
        }
        <TrackerPositionHeatmapModal
            open={isHeatmapModalOpen}
            onClose={handleCloseHeatmapModal}
            onExited={restoreHeatmapFocus}
            title="Histórico de Posicionamento"
            period={ncuPeriod}
            onPeriodChange={handleNcuPeriodChange}
            contextId={hasCurrentPositionContext ? positionContext?.contextId ?? null : null}
            status={hasCurrentPositionContext ? positionView.status : "loading"}
            refreshing={hasCurrentPositionContext && positionView.refreshing}
            series={visiblePositionSeries}
            isMobile={window.innerWidth < 771}
        />
        </>
    )
}

export default TrackersPage;
